import { Component, Optional, OnInit, OnDestroy } from '@angular/core';
import { NbDialogRef, NbCardModule, NbButtonModule, NbSpinnerModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { KPI } from '../../../@core/api';
import { ComplianceService } from '../../../@core/utils/compliance.service';
import { SaveToastService } from '../../../@core/utils';
import { of, forkJoin } from 'rxjs';
import { map, catchError, first, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-control-report-list-dialog',
  templateUrl: './control-report-list-dialog.component.html',
  styleUrls: ['./control-report-list-dialog.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbSpinnerModule,
    TranslateModule,
    CommonModule,
    FontAwesomeModule
  ],
  providers: [DatePipe]
})
export class ControlReportListDialogComponent implements OnInit, OnDestroy {
  public reports: KPI.ControlOfficeReport[] = [];
  public loading: boolean = false;
  public asset: KPI.MappedAsset | null = null;
  public onReportAttached: (() => void) | null = null;

  constructor(
    @Optional() public ref: NbDialogRef<ControlReportListDialogComponent>,
    private complianceService: ComplianceService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private saveToast: SaveToastService
  ) {}

  ngOnInit(): void {
    const context = (this.ref as any)?.context;
    if (context) {
      this.asset = context.asset || null;
      this.onReportAttached = context.onReportAttached || null;
    }
    this.loadReports();
  }

  ngOnDestroy(): void {}

  public loadReports(): void {
    this.loading = true;
    
    let siteId: number | null = null;
    if (this.asset?.perimeters && this.asset.perimeters.length > 0) {
      const site = this.asset.perimeters.find(p => !p.level_parent || !p.is_monosite) 
        || this.asset.perimeters[0];
      siteId = site.id;
    }

    const params = siteId ? { perimeter: siteId } : undefined;
    
    this.complianceService.listControlOfficeReports(params)
      .pipe(
        map(response => {
          let reports: KPI.ControlOfficeReport[] = [];
          if (response && typeof response === 'object') {
            if (Array.isArray(response)) {
              reports = response;
            } else if ('results' in response && Array.isArray((response as any).results)) {
              reports = (response as any).results;
            }
          }
          return reports.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          });
        }),
        catchError(() => {
          return of([]);
        })
      )
      .subscribe(reports => {
        this.reports = reports || [];
        this.loading = false;
      });
  }

  public getLinkedEquipmentText(report: KPI.ControlOfficeReport): string {
    if (!report.linked_equipment || !Array.isArray(report.linked_equipment)) {
      return this.translate.instant('asset_form_drawer.compliance.control_report.no_linked_equipment');
    }
    const count = report.linked_equipment.length;
    if (count === 0) {
      return this.translate.instant('asset_form_drawer.compliance.control_report.no_linked_equipment');
    }
    return this.translate.instant('asset_form_drawer.compliance.control_report.multiple_linked_equipment', { count });
  }

  public isAlreadyLinked(report: KPI.ControlOfficeReport): boolean {
    if (!this.asset?.id || !report.linked_equipment || !Array.isArray(report.linked_equipment)) {
      return false;
    }
    return report.linked_equipment.includes(this.asset.id);
  }

  public formatDate(date: string | Date): string {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';
      return this.datePipe.transform(dateObj, 'd MMM yyyy', undefined, this.translate.currentLang) || '-';
    } catch (error) {
      return '-';
    }
  }

  public attachReport(report: KPI.ControlOfficeReport): void {
    if (!this.asset?.id) {
      return;
    }

    if (this.isAlreadyLinked(report)) {
      return;
    }

    const currentLinkedEquipment = report.linked_equipment && Array.isArray(report.linked_equipment) 
      ? [...report.linked_equipment] 
      : [];
    
    if (!currentLinkedEquipment.includes(this.asset.id)) {
      currentLinkedEquipment.push(this.asset.id);
    }

    this.complianceService.patchControlOfficeReport(report.id, {
      linked_equipment: currentLinkedEquipment
    }).pipe(
      first(),
      switchMap(updatedReport => {
        if (!updatedReport) {
          return of(null);
        }
        
        const compliance = (this.asset as any)?.compliance;
        let complianceId: number | null = null;
        if (compliance) {
          complianceId = typeof compliance === 'object' && compliance.id ? compliance.id : compliance;
          complianceId = complianceId && complianceId > 0 ? complianceId : null;
        }
        
        if (!complianceId) {
          return of(updatedReport);
        }
        
        return this.complianceService.getReportReservations(report.id).pipe(
          first(),
          switchMap(reserves => {
            if (!reserves || reserves.length === 0) {
              return of(updatedReport);
            }
            
            const reserveUpdateRequests = reserves.map(reserve => {
              return this.complianceService.updateReservation(reserve.id, {
                compliance: complianceId!
              } as KPI.ReservationUpdatePayload).pipe(
                catchError(() => of(null))
              );
            });
            
            return forkJoin(reserveUpdateRequests).pipe(
              map(() => updatedReport),
              catchError(() => of(updatedReport))
            );
          }),
          catchError(() => of(updatedReport))
        );
      }),
      catchError(() => {
        return of(null);
      })
    ).subscribe(updatedReport => {
      if (updatedReport) {
        this.updateComplianceDate(report.date);
        this.saveToast.showSaved();
        if (this.onReportAttached) {
          this.onReportAttached();
        }
        this.ref.close(true);
      }
    });
  }

  private updateComplianceDate(reportDate: string | Date): void {
    if (!this.asset?.id) {
      return;
    }

    const compliance = (this.asset as any).compliance;
    if (!compliance || !compliance.id) {
      return;
    }

    const dateStr = typeof reportDate === 'string' 
      ? reportDate 
      : this.datePipe.transform(reportDate, 'yyyy-MM-dd') || null;

    if (!dateStr) {
      return;
    }

    this.complianceService.patchAssetCompliance(compliance.id, {
      last_control_date: dateStr
    })      .pipe(
        first(),
        catchError(() => {
          return of(null);
        })
      ).subscribe();
  }

  public cancel(): void {
    this.ref.close(false);
  }
}

